<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue';
import logoDark from '../assets/images/logo-dark.png';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import type { Locale } from '../i18n';
import {
  LogOut,
  Menu,
  MessageSquare,
  X,
  Calendar,
  Briefcase,
  Settings,
  ChevronDown,
  Users,
  CalendarDays,
} from 'lucide-vue-next';
import { useAuth } from '../composables/useAuth';
import { useGoogleCalendar } from '../composables/useGoogleCalendar';
import { useMessengerFacebook } from '../composables/useMessengerFacebook';
import {
  headerState,
  initHeaderState,
  refreshCalendarStatus,
  refreshMessengerStatus,
} from '../composables/useHeaderState';

const router = useRouter();
const { locale } = useI18n();
const { user, logout: authLogout } = useAuth();

const switchLocale = (lang: Locale) => {
  locale.value = lang;
  localStorage.setItem('locale', lang);
};
const { connect: connectCalendar, disconnect: disconnectCalendar } = useGoogleCalendar();
const { connect: connectMessenger, disconnect: disconnectMessenger } = useMessengerFacebook();

const isMobileMenuOpen = ref(false);
const isUserMenuOpen = ref(false);
const userMenuRef = ref<HTMLElement | null>(null);
const headerEl = ref<HTMLElement | null>(null);
const headerHeight = ref(0);
let resizeObserver: ResizeObserver | null = null;

// Use global state
const isCalendarConnected = headerState.isCalendarConnected;
const isCalendarLoading = headerState.isCalendarLoading;
const isMessengerConnected = headerState.isMessengerConnected;
const isMessengerLoading = headerState.isMessengerLoading;
const messengerSourceId = headerState.messengerSourceId;

const handleClickOutside = (e: MouseEvent) => {
  if (userMenuRef.value && !userMenuRef.value.contains(e.target as Node)) {
    isUserMenuOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  if (headerEl.value) {
    resizeObserver = new ResizeObserver(() => {
      headerHeight.value = headerEl.value?.offsetHeight ?? 0;
    });
    resizeObserver.observe(headerEl.value);
    headerHeight.value = headerEl.value.offsetHeight;
  }
});
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  resizeObserver?.disconnect();
});

const handleMessengerClick = async () => {
  if (isMessengerLoading.value || !user.value || !messengerSourceId.value) return;
  isUserMenuOpen.value = false;
  isMobileMenuOpen.value = false;

  if (isMessengerConnected.value) {
    await disconnectMessenger(user.value.organization_id);
    await refreshMessengerStatus(user.value.organization_id);
  } else {
    await connectMessenger(user.value.organization_id, messengerSourceId.value);
    setTimeout(() => {
      if (user.value?.organization_id) {
        refreshMessengerStatus(user.value.organization_id);
      }
    }, 1000);
  }
};

const handleCalendarClick = async () => {
  if (isCalendarLoading.value) return;
  isUserMenuOpen.value = false;
  isMobileMenuOpen.value = false;

  if (isCalendarConnected.value) {
    await disconnectCalendar();
    await refreshCalendarStatus();
  } else {
    await connectCalendar();
  }
};

const handleLogout = () => {
  isUserMenuOpen.value = false;
  isMobileMenuOpen.value = false;
  authLogout();
  router.push('/login');
};

const handleLogoClick = () => {
  router.push('/messenger');
};

const handleConversationsClick = () => {
  isUserMenuOpen.value = false;
  isMobileMenuOpen.value = false;
  // The conversation list is part of the home page, so there is no separate route.
  router.push('/messenger');
};

const handleServicesClick = () => {
  isUserMenuOpen.value = false;
  isMobileMenuOpen.value = false;
  router.push('/services');
};

const handleSettingsClick = () => {
  isUserMenuOpen.value = false;
  isMobileMenuOpen.value = false;
  router.push('/settings');
};

const handleClientsClick = () => {
  isUserMenuOpen.value = false;
  isMobileMenuOpen.value = false;
  router.push('/clients');
};

const handleVisitsClick = () => {
  isUserMenuOpen.value = false;
  isMobileMenuOpen.value = false;
  router.push('/visits');
};

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
};

defineProps<{
  activePage?: 'messenger' | 'services' | 'settings' | 'clients' | 'visits';
}>();

// Watch for user changes and initialize state
watch(
  () => user.value,
  (newUser) => {
    if (newUser) {
      initHeaderState(newUser.id, newUser.organization_id);
    }
  },
  { immediate: true },
);
</script>

<template>
  <div ref="headerEl" class="bg-[#111113] border-b border-[#2A2018] sticky top-0 z-50">
    <div class="px-4 md:px-6 py-4">
      <div class="flex items-center justify-between">
        <!-- Left side: Hamburger (mobile) + Logo + Rozmowy -->
        <div class="flex items-center gap-3">
          <!-- Hamburger Menu Button (mobile only) -->
          <button
            @click="toggleMobileMenu"
            class="lg:hidden p-2 hover:bg-[#18181C] rounded-lg transition"
            :aria-label="$t('header.toggle_menu')"
          >
            <Menu v-if="!isMobileMenuOpen" class="w-6 h-6 text-[#F5F0EA]" />
            <X v-else class="w-6 h-6 text-[#F5F0EA]" />
          </button>

          <!-- Logo -->
          <div
            @click="handleLogoClick"
            class="flex items-center gap-3 cursor-pointer hover:opacity-80 transition"
          >
            <img :src="logoDark" alt="Assistly" class="h-10 object-contain" />
          </div>
        </div>

        <!-- Right side: User badge (mobile: non-clickable) / dropdown (desktop) -->
        <div v-if="user" ref="userMenuRef" class="relative flex items-center gap-2">
          <!-- Language flag (both mobile and desktop) -->
          <button
            @click.stop="switchLocale(locale === 'pl' ? 'en' : 'pl')"
            :title="locale === 'pl' ? $t('language.en') : $t('language.pl')"
            class="text-xl leading-none hover:opacity-60 transition opacity-90 px-1"
          >
            {{ locale === 'pl' ? '🇺🇸' : '🇵🇱' }}
          </button>

          <!-- Mobile: plain avatar, not clickable -->
          <div class="lg:hidden flex items-center gap-2 px-3 py-2 text-sm text-[#F5F0EA]/70">
            <div
              class="w-8 h-8 bg-[#D4956A] rounded-full flex items-center justify-center flex-shrink-0"
            >
              <span class="text-[#0A0A0B] text-xs font-medium">
                {{ user.first_name[0] }}{{ user.last_name[0] }}
              </span>
            </div>
          </div>

          <!-- Desktop: clickable dropdown trigger -->
          <button
            @click.stop="toggleUserMenu"
            class="hidden lg:flex items-center gap-2 px-3 py-2 rounded-2xl hover:bg-[#18181C] transition text-sm text-[#F5F0EA]"
          >
            <div
              class="w-8 h-8 bg-[#D4956A] rounded-full flex items-center justify-center flex-shrink-0"
            >
              <span class="text-[#0A0A0B] text-xs font-medium">
                {{ user.first_name[0] }}{{ user.last_name[0] }}
              </span>
            </div>
            <span>{{ user.first_name }} {{ user.last_name }}</span>
            <ChevronDown
              :class="['w-4 h-4 transition-transform', isUserMenuOpen ? 'rotate-180' : '']"
            />
          </button>

          <!-- Dropdown menu -->
          <div
            v-if="isUserMenuOpen"
            class="absolute right-0 top-full mt-1 w-52 bg-[#18181C] rounded-2xl shadow-lg border border-[#2A2018] py-1 z-50"
          >
            <!-- Rozmowy -->
            <button
              @click="handleConversationsClick"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#F5F0EA] hover:bg-[#1E1E23] transition"
            >
              <MessageSquare class="w-4 h-4 text-[#D4956A]" />
              <span>{{ $t('header.conversations') }}</span>
            </button>

            <!-- Usługi -->
            <button
              @click="handleServicesClick"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#F5F0EA] hover:bg-[#1E1E23] transition"
            >
              <Briefcase class="w-4 h-4 text-[#D4956A]" />
              <span>{{ $t('header.services') }}</span>
            </button>

            <!-- Klienci -->
            <button
              @click="handleClientsClick"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#F5F0EA] hover:bg-[#1E1E23] transition"
            >
              <Users class="w-4 h-4 text-[#D4956A]" />
              <span>{{ $t('header.clients') }}</span>
            </button>

            <!-- Wizyty -->
            <button
              @click="handleVisitsClick"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#F5F0EA] hover:bg-[#1E1E23] transition"
            >
              <CalendarDays class="w-4 h-4 text-[#D4956A]" />
              <span>{{ $t('header.visits') }}</span>
            </button>

            <!-- Ustawienia -->
            <button
              @click="handleSettingsClick"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#F5F0EA] hover:bg-[#1E1E23] transition"
            >
              <Settings class="w-4 h-4 text-[#F5F0EA]/50" />
              <span>{{ $t('header.settings') }}</span>
            </button>

            <!-- Messenger -->
            <button
              @click="handleMessengerClick"
              :disabled="isMessengerLoading || !messengerSourceId"
              :title="
                isMessengerConnected
                  ? $t('header.messenger_disconnect_tooltip')
                  : !messengerSourceId
                    ? $t('header.messenger_no_source_tooltip')
                    : $t('header.messenger_connect_tooltip')
              "
              class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#F5F0EA] hover:bg-[#1E1E23] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div
                v-if="isMessengerLoading"
                class="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
              ></div>
              <svg
                v-else
                class="w-4 h-4 flex-shrink-0"
                :class="isMessengerConnected ? 'text-[#524840]' : 'text-[#D4956A]'"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"
                />
              </svg>
              <span>{{
                isMessengerConnected
                  ? $t('header.messenger_disconnect')
                  : $t('header.messenger_connect')
              }}</span>
            </button>

            <!-- Kalendarz -->
            <button
              @click="handleCalendarClick"
              :disabled="isCalendarLoading"
              :title="
                isCalendarConnected
                  ? $t('header.calendar_disconnect_tooltip')
                  : $t('header.calendar_connect_tooltip')
              "
              class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#F5F0EA] hover:bg-[#1E1E23] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div
                v-if="isCalendarLoading"
                class="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
              ></div>
              <Calendar
                v-else
                :class="['w-4 h-4', isCalendarConnected ? 'text-[#524840]' : 'text-[#D4956A]']"
              />
              <span>{{
                isCalendarConnected
                  ? $t('header.calendar_disconnect')
                  : $t('header.calendar_connect')
              }}</span>
            </button>

            <div class="border-t border-[#2A2018] my-1"></div>

            <!-- Wyloguj -->
            <button
              @click="handleLogout"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-red-900/20 transition"
            >
              <LogOut class="w-4 h-4" />
              <span>{{ $t('header.logout') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Mobile menu overlay (drawer from left) -->
  <Transition name="slide-left">
    <div
      v-if="isMobileMenuOpen"
      class="lg:hidden fixed left-0 w-full z-40 bg-[#111113] overflow-y-auto px-4 py-2 flex flex-col gap-1"
      :style="{ top: headerHeight + 'px', height: `calc(100vh - ${headerHeight}px)` }"
    >
      <!-- Rozmowy -->
      <button
        @click="handleConversationsClick"
        :class="[
          'flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm w-full transition',
          activePage === 'messenger'
            ? 'bg-[#2A2018] text-[#D4956A]'
            : 'text-[#F5F0EA] hover:bg-[#18181C]',
        ]"
      >
        <MessageSquare class="w-4 h-4" />
        <span>{{ $t('header.conversations') }}</span>
      </button>

      <!-- Usługi -->
      <button
        @click="handleServicesClick"
        :class="[
          'flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm w-full transition',
          activePage === 'services'
            ? 'bg-[#2A2018] text-[#D4956A]'
            : 'text-[#F5F0EA] hover:bg-[#18181C]',
        ]"
      >
        <Briefcase class="w-4 h-4" />
        <span>{{ $t('header.services') }}</span>
      </button>

      <!-- Klienci -->
      <button
        @click="handleClientsClick"
        :class="[
          'flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm w-full transition',
          activePage === 'clients'
            ? 'bg-[#2A2018] text-[#D4956A]'
            : 'text-[#F5F0EA] hover:bg-[#18181C]',
        ]"
      >
        <Users class="w-4 h-4" />
        <span>{{ $t('header.clients') }}</span>
      </button>

      <!-- Wizyty -->
      <button
        @click="handleVisitsClick"
        :class="[
          'flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm w-full transition',
          activePage === 'visits'
            ? 'bg-[#2A2018] text-[#D4956A]'
            : 'text-[#F5F0EA] hover:bg-[#18181C]',
        ]"
      >
        <CalendarDays class="w-4 h-4" />
        <span>{{ $t('header.visits') }}</span>
      </button>

      <!-- Ustawienia -->
      <button
        @click="handleSettingsClick"
        :class="[
          'flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm w-full transition',
          activePage === 'settings'
            ? 'bg-[#1E1E23] text-[#F5F0EA]'
            : 'text-[#F5F0EA] hover:bg-[#18181C]',
        ]"
      >
        <Settings class="w-4 h-4" />
        <span>{{ $t('header.settings') }}</span>
      </button>

      <!-- Messenger -->
      <button
        @click="handleMessengerClick"
        :disabled="isMessengerLoading || !messengerSourceId"
        class="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm w-full transition text-[#F5F0EA] hover:bg-[#18181C] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <div
          v-if="isMessengerLoading"
          class="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
        ></div>
        <svg
          v-else
          class="w-4 h-4"
          :class="isMessengerConnected ? 'text-[#524840]' : 'text-[#D4956A]'"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"
          />
        </svg>
        <span>{{
          isMessengerConnected ? $t('header.messenger_disconnect') : $t('header.messenger_connect')
        }}</span>
      </button>

      <!-- Kalendarz -->
      <button
        @click="handleCalendarClick"
        :disabled="isCalendarLoading"
        class="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm w-full transition text-[#F5F0EA] hover:bg-[#18181C] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <div
          v-if="isCalendarLoading"
          class="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
        ></div>
        <Calendar
          v-else
          :class="['w-4 h-4', isCalendarConnected ? 'text-[#524840]' : 'text-[#D4956A]']"
        />
        <span>{{
          isCalendarConnected ? $t('header.calendar_disconnect') : $t('header.calendar_connect')
        }}</span>
      </button>

      <div class="border-t border-[#2A2018] my-1"></div>

      <!-- Wyloguj -->
      <button
        @click="handleLogout"
        class="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm w-full transition text-red-400 hover:bg-red-900/20"
      >
        <LogOut class="w-4 h-4" />
        <span>{{ $t('header.logout') }}</span>
      </button>
    </div>
  </Transition>
</template>

<style scoped>
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.3s ease;
}

.slide-left-enter-from,
.slide-left-leave-to {
  transform: translateX(-100%);
}
</style>
